/**
 * What a time is being held against.
 *
 * A lap time on its own says almost nothing. Two minutes fourteen is quick or
 * slow depending on the circuit, the class, the fuel load and who else is out,
 * and the number people actually act on is the difference. So every figure the
 * stats page shows gets a second line saying what it is compared to and by how
 * much.
 *
 * The three baselines answer three different questions:
 *
 *   session   — "is the car quicker than it has been today"
 *   class     — "are we competitive with the people we are racing"
 *   event     — "is this our best of the weekend, or did we do better in
 *                practice" — which is where an unrepresentative qualifying
 *                lap or a session run in the rain gets caught
 *
 * Deliberately not "the outright fastest car" as a default. In a multi-class
 * field that is usually a car in a different class doing a different job, and a
 * delta to it is a number nobody can act on.
 */
export type Baseline = 'session' | 'class' | 'event'

export const baselineLabels: Record<Baseline, string> = {
  session: 'this car, this session',
  class: 'best in class, this session',
  event: 'this car, whole event',
}


/** The four figures the stats page compares. */
import type { CarSummaryDoc } from './timing'
export type { CarSummaryDoc }

export type Measure = 'lap' | 's1' | 's2' | 's3' | 'ideal'

export function measureOf(s: CarSummaryDoc | null | undefined, m: Measure): number | null {
  if (!s) return null
  const v =
    m === 'lap'
      ? s.bestLapMs
      : m === 's1'
        ? s.bestS1Ms
        : m === 's2'
          ? s.bestS2Ms
          : m === 's3'
            ? s.bestS3Ms
            : ideal(s)
  return v != null && v > 0 ? v : null
}

/**
 * The ideal lap, never slower than a lap the car has actually driven.
 *
 * A timing system measures sectors and lap times separately and they do not
 * have to agree. Car 439 at Zandvoort had a best lap of 1:58.892 whose own
 * three sectors added up to 1:59.103, so the "ideal" came out two tenths slower
 * than the lap it was made of — six of thirty-seven cars in that session, and
 * a self-evidently impossible number on a page read to decide whether there is
 * time left in the car.
 *
 * The interpreter caps it at source now, so this is for everything written
 * before it did: months of stored summaries that only a reprocess would
 * correct, and which are read far more often than they are rebuilt. Cheap, and
 * it makes the invariant true wherever the figure is shown rather than only
 * where it happens to have been recomputed.
 */
function ideal(s: CarSummaryDoc): number | null | undefined {
  const sum = s.idealLapMs
  const best = s.bestLapMs
  if (sum == null || best == null || best <= 0) return sum
  return best < sum ? best : sum
}

export interface Comparison {
  /** The figure being compared against, or null when there is nothing to compare to. */
  ms: number | null
  /** Ours minus theirs. Negative is quicker. Null when either side is missing. */
  deltaMs: number | null
  /** Where the baseline came from, for a caption. */
  label: string
}

/**
 * Hold one figure against a baseline.
 *
 * Returns nulls rather than zeroes when there is nothing to compare to: a
 * missing baseline and an exact match are entirely different things, and
 * showing "+0.000" for the former is a confident lie.
 *
 * Comparing a value against itself — this car's best lap against this car's
 * session best, which is the same number — yields a delta of zero, and the
 * caller is expected not to draw that. `isSelf` says so explicitly rather than
 * leaving it to be inferred from a zero that might be a genuine dead heat.
 */
export function compare(
  ours: number | null,
  baseline: number | null,
  label: string,
): Comparison & { isSelf: boolean } {
  if (ours == null || baseline == null) {
    return { ms: baseline, deltaMs: null, label, isSelf: false }
  }
  return { ms: baseline, deltaMs: ours - baseline, label, isSelf: ours === baseline }
}

/**
 * The best of a set of summaries for one measure.
 *
 * Used for "best in class" and for "this car across the event", which are the
 * same operation over different sets — the difference is entirely in what the
 * caller passes in, which is why this takes summaries rather than a query.
 */
export function bestOf(
  summaries: readonly CarSummaryDoc[],
  m: Measure,
): { ms: number; from: CarSummaryDoc } | null {
  let best: { ms: number; from: CarSummaryDoc } | null = null
  for (const s of summaries) {
    const v = measureOf(s, m)
    if (v == null) continue
    if (best === null || v < best.ms) best = { ms: v, from: s }
  }
  return best
}

/**
 * A signed difference, as a pit wall writes it.
 *
 * Always signed, including the plus, because an unsigned "0.348" beside a lap
 * time reads as a time rather than as a gap. Three decimals: timing is reported
 * to the millisecond and rounding a gap to tenths loses the thing that
 * distinguishes two laps of the same second.
 */
export function formatDelta(ms: number | null | undefined): string {
  if (ms == null) return '—'
  const sign = ms > 0 ? '+' : ms < 0 ? '−' : '±'
  const abs = Math.abs(ms)
  // Past a minute a raw seconds figure stops being readable — a car 83 seconds
  // down reads better as 1:23 than as 83.000.
  if (abs >= 60_000) {
    const m = Math.floor(abs / 60_000)
    const s = (abs % 60_000) / 1000
    return `${sign}${m}:${s.toFixed(3).padStart(6, '0')}`
  }
  return `${sign}${(abs / 1000).toFixed(3)}`
}
